const initAlpineStoreIsMobile = () => {
  if (!window.Alpine) return;

  // store が無ければ作成、あれば再利用
  if (!Alpine.store("device")) {
    Alpine.store("device", { mobile: false });
  }

  const update = () => {
    Alpine.store("device").mobile =
      window.matchMedia("(max-width: 768px)").matches;
  };

  update();

  // 二重登録防止（1回だけ）
  if (window.__isMobileResizeBound) return;
  window.__isMobileResizeBound = true;

  window.addEventListener("resize", update);
};

const createScrollLockController = () => {
  let lockCount = 0;
  let scrollTop = 0;

  const getScrollbarWidth = () =>
    window.innerWidth - document.documentElement.clientWidth;

  const setScrollbarWidth = (width) => {
    document.documentElement.style.setProperty(
      "--scrollbar-width",
      width ? `${width}px` : "0px",
    );
  };

  const applyLock = () => {
    const body = document.body;
    const scrollbarWidth = getScrollbarWidth();

    scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    setScrollbarWidth(scrollbarWidth);
    body.style.position = "fixed";
    body.style.top = `-${scrollTop}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.paddingRight = `${scrollbarWidth}px`;
  };

  const applyUnlock = () => {
    const body = document.body;

    setScrollbarWidth(0);
    body.style.position = "";
    body.style.top = "";
    body.style.left = "";
    body.style.right = "";
    body.style.width = "";
    body.style.paddingRight = "";

    const html = document.documentElement;
    const previousScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    window.scrollTo(0, scrollTop);
    html.style.scrollBehavior = previousScrollBehavior;
  };

  return {
    acquire() {
      if (lockCount === 0) {
        applyLock();
      }

      lockCount += 1;
    },

    release() {
      if (lockCount === 0) return;

      lockCount -= 1;

      if (lockCount === 0) {
        applyUnlock();
      }
    },
  };
};

const scrollLockController = createScrollLockController();

const createScrollLockMixin = () => ({
  scrollLocked: false,
  unlockTimer: null,

  lockScroll() {
    if (this.scrollLocked) return;

    scrollLockController.acquire();
    this.scrollLocked = true;
  },

  unlockScroll() {
    if (!this.scrollLocked) return;

    scrollLockController.release();
    this.scrollLocked = false;
  },

  clearUnlockTimer() {
    if (!this.unlockTimer) return;

    clearTimeout(this.unlockTimer);
    this.unlockTimer = null;
  },

  scheduleUnlock(callback, transitionMs) {
    this.clearUnlockTimer();

    this.unlockTimer = setTimeout(() => {
      this.unlockTimer = null;
      callback();
    }, transitionMs);
  },
});

const hamMenu = () => {
  const transitionMs = 300;

  return {
    toggleMenu: false,
    ...createScrollLockMixin(),

    openMenu() {
      this.clearUnlockTimer();
      this.toggleMenu = true;
    },

    closeMenu() {
      if (!this.toggleMenu) return;

      this.toggleMenu = false;
      this.scheduleUnlock(() => {
        if (!this.toggleMenu) {
          this.unlockScroll();
        }
      }, transitionMs);
    },

    init() {
      this.$watch("toggleMenu", (value) => {
        if (value) {
          this.clearUnlockTimer();
          this.lockScroll();
        }
      });
    },
  };
};

const recruitEntryModal = () => {
  const transitionMs = 300;

  return {
    open: false,
    ...createScrollLockMixin(),

    openModal() {
      this.clearUnlockTimer();
      this.open = true;
    },

    closeModal() {
      if (!this.open) return;

      this.open = false;
      this.scheduleUnlock(() => {
        if (!this.open) {
          this.unlockScroll();
        }
      }, transitionMs);
    },

    onLeaveEnd() {
      if (!this.open) {
        this.unlockScroll();
      }
    },

    init() {
      this.$watch("open", (value) => {
        if (value) {
          this.clearUnlockTimer();
          this.lockScroll();
        }
      });
    },
  };
};

const trainingModal = () => {
  if (!Alpine.store("training")) {
    Alpine.store("training", { modal: false });
  }

  return {
    current: 1,
    modalSwiper: null,

    getModalElements() {
      const modalEl = document.querySelector(".p-training__modal");
      if (!modalEl) return null;

      const swiperEl = modalEl.querySelector(".js-story-swiper");
      const prevEl = modalEl.querySelector(".js-story-swiper__prev");
      const nextEl = modalEl.querySelector(".js-story-swiper__next");

      if (!swiperEl || !prevEl || !nextEl) return null;

      return { modalEl, swiperEl, prevEl, nextEl };
    },

    initOrUpdateSwiper() {
      const els = this.getModalElements();
      if (!els) return;

      const { swiperEl, prevEl, nextEl } = els;

      if (!this.modalSwiper) {
        this.modalSwiper = new Swiper(swiperEl, {
          loop: true,
          slidesPerView: "auto",
          observer: true,
          observeParents: true,
          navigation: {
            prevEl,
            nextEl,
          },
        });
      } else {
        this.modalSwiper.params.navigation.prevEl = prevEl;
        this.modalSwiper.params.navigation.nextEl = nextEl;

        this.modalSwiper.navigation.destroy();
        this.modalSwiper.navigation.init();
        this.modalSwiper.navigation.update();
        this.modalSwiper.update();
      }
    },

    modalTo() {
      Alpine.store("training").modal = true;

      Alpine.nextTick(() => {
        setTimeout(() => {
          this.initOrUpdateSwiper();

          if (this.modalSwiper) {
            this.modalSwiper.slideToLoop(this.current - 1, 0);
            this.modalSwiper.update();
            this.modalSwiper.navigation.update();
          }
        }, 50);
      });
    },

    closeModal() {
      Alpine.store("training").modal = false;
    },
  };
};

const programDateFilter = () => {
  return {
    selectedProgramId: "",

    init() {
      this.syncSelectedProgram();
      this.toggleDateItems();

      this.$root.addEventListener("change", (event) => {
        const target = event.target;

        if (
          target.matches(
            '.wpcf7-form-control-wrap[data-name="program-list"] input[type="radio"]',
          )
        ) {
          this.selectedProgramId = target.dataset.programId || "";
          this.resetDateSelection();
          this.toggleDateItems();
        }
      });

      const wpcf7El = this.$root.closest(".wpcf7");
      if (!wpcf7El) return;

      wpcf7El.addEventListener("wpcf7invalid", () => {
        this.$nextTick(() => {
          this.syncSelectedProgram();
          this.toggleDateItems();
        });
      });

      wpcf7El.addEventListener("wpcf7reset", () => {
        this.$nextTick(() => {
          this.selectedProgramId = "";
          this.resetDateSelection();
          this.toggleDateItems();
        });
      });

      wpcf7El.addEventListener("wpcf7mailsent", () => {
        this.$nextTick(() => {
          this.selectedProgramId = "";
          this.resetDateSelection();
          this.toggleDateItems();
        });
      });
    },

    syncSelectedProgram() {
      const checkedInput = this.$root.querySelector(
        '.wpcf7-form-control-wrap[data-name="program-list"] input[type="radio"]:checked',
      );

      this.selectedProgramId = checkedInput?.dataset.programId || "";
    },

    toggleDateItems() {
      const dateItems = this.$root.querySelectorAll(
        '.wpcf7-form-control-wrap[data-name="program-date"] .wpcf7-list-item',
      );

      dateItems.forEach((item) => {
        const itemProgramId = item.dataset.programId || "";
        const input = item.querySelector('input[type="radio"]');
        const shouldShow =
          this.selectedProgramId !== "" &&
          itemProgramId === this.selectedProgramId;

        item.hidden = !shouldShow;

        if (input) {
          input.disabled = !shouldShow;

          if (!shouldShow) {
            input.checked = false;
          }
        }
      });
    },

    resetDateSelection() {
      const dateInputs = this.$root.querySelectorAll(
        '.wpcf7-form-control-wrap[data-name="program-date"] input[type="radio"]',
      );

      dateInputs.forEach((input) => {
        input.checked = false;
      });
    },
  };
};

const dateSelectFilter = () => {
  return {
    init() {
      this.updateDays();

      this.$root.addEventListener("change", (event) => {
        const target = event.target;

        if (
          target.matches('[name="birth-year"]') ||
          target.matches('[name="birth-month"]')
        ) {
          this.updateDays();
        }
      });
    },

    updateDays() {
      const yearEl = this.$root.querySelector('[name="birth-year"]');
      const monthEl = this.$root.querySelector('[name="birth-month"]');
      const dayEl = this.$root.querySelector('[name="birth-day"]');

      if (!yearEl || !monthEl || !dayEl) return;

      const year = Number(yearEl.value);
      const month = Number(monthEl.value);

      if (!year || !month) {
        this.resetDayOptions(dayEl);
        return;
      }

      const maxDay = new Date(year, month, 0).getDate();

      [...dayEl.options].forEach((option) => {
        if (option.value === "") return;

        const day = Number(option.value);
        const isValid = day <= maxDay;

        option.hidden = !isValid;
        option.disabled = !isValid;
      });

      if (dayEl.value && Number(dayEl.value) > maxDay) {
        dayEl.value = "";
      }
    },

    resetDayOptions(dayEl) {
      [...dayEl.options].forEach((option) => {
        if (option.value === "") return;
        option.hidden = false;
        option.disabled = false;
      });
    },
  };
};

document.addEventListener("alpine:init", () => {
  initAlpineStoreIsMobile();
  Alpine.data("hamMenu", hamMenu);
  Alpine.data("recruitEntryModal", recruitEntryModal);
  Alpine.data("trainingModal", trainingModal);
  Alpine.data("programDateFilter", programDateFilter);
  Alpine.data("dateSelectFilter", dateSelectFilter);
});
